import { useQuery } from "react-query";
import { Conversion, fetchConversion } from "@jammable/clientapi";

const POLL_INTERVAL = 2500;

export function useConversion(conversion: Conversion) {
  // we allow for the conversion to be preloaded
  // if it is ready we return its values
  // otherwise we poll until it is ready
  const {
    shortid: conversionId,
    status,
    videoSettings,
    outputUrls,
  } = conversion;
  const isPreloadedAndReady = status === "success" || status === "failed";

  const hasVideoTemplate = videoSettings?.template || videoSettings?.externalTemplate;
  const enabled =
    !isPreloadedAndReady ||
    (hasVideoTemplate && videoSettings?.progress !== undefined && !outputUrls?.video);

  const hasAudioRendered = (data: any) => {
    return (
      (data?.status === "failed" || data?.status === "success") &&
      data?.outputUrls?.audio
    );
  };

  const hasVideoRendered = (data: any) => {
    const vs = data?.videoSettings || videoSettings;
    const hasTemplate = vs?.template || vs?.externalTemplate;
    return (
      (vs?.progress === undefined && !hasTemplate) ||
      vs?.progress === 100
    );
  };

  const { data } = useQuery<unknown, Error, Conversion>({
    enabled: enabled,
    queryKey: ["conversionId", conversionId],
    refetchInterval: (data) =>
      hasAudioRendered(data) && hasVideoRendered(data) ? false : POLL_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      // If there's an external video in progress, trigger a poll to update DB progress
      // before fetching the conversion, so we get the latest state
      const currentVs = data?.videoSettings || videoSettings;
      if (
        currentVs?.externalVideoId &&
        currentVs?.progress != null &&
        currentVs.progress > 0 &&
        currentVs.progress < 100
      ) {
        try {
          await fetch(
            `/api/video/pollExternalVideo?conversionId=${data?._id || conversion._id}`,
          );
        } catch (e) {
          // Ignore poll errors — fetchConversion will still work
        }
      }
      return fetchConversion(conversionId);
    },
  });

  return {
    ...(isPreloadedAndReady || !data ? conversion : data),
    isErrored: conversion?.status === "failed" || data?.status === "failed",
    isComplete: data?.status === "success" || conversion?.status === "success",
  };
}
